import React from 'react';
import { ArrowUpCircle, ArrowDownCircle, Plus, RefreshCw, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { formatCurrency } from '../../lib/utils';

const UNIT_LABELS = {
  lb: 'Libras (lb)',
  kg: 'Kilogramos (kg)',
  qq: 'Quintales (qq)',
  ton: 'Toneladas (ton)',
  saco: 'Sacos',
};

export function MovementDialog({ 
  formData, 
  setFormData, 
  onSubmit,
  materials,
  suppliers,
  conversionPreview,
  onAddSupplier
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="add-movement-btn">
          <ArrowDownCircle className="h-4 w-4 mr-2" />
          Registrar Compra / Movimiento
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {formData.type === 'entrada' ? (
              <>
                <ArrowUpCircle className="h-5 w-5 text-green-600" />
                Registrar Compra de Materia Prima
              </>
            ) : (
              <>
                <ArrowDownCircle className="h-5 w-5 text-red-600" />
                Registrar Salida de Inventario
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Tipo de movimiento */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Movimiento *</Label>
              <Select 
                value={formData.type} 
                onValueChange={(v) => setFormData({...formData, type: v})}
              >
                <SelectTrigger data-testid="movement-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada">Entrada (Compra)</SelectItem>
                  <SelectItem value="salida">Salida (Uso/Producción)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Materia Prima *</Label>
              <Select 
                value={formData.material_id} 
                onValueChange={(v) => setFormData({...formData, material_id: v})}
              >
                <SelectTrigger data-testid="movement-material-select">
                  <SelectValue placeholder="Seleccionar material" />
                </SelectTrigger>
                <SelectContent>
                  {materials.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} (Stock: {m.current_stock} {m.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cantidad y Unidad */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cantidad *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                onFocus={(e) => e.target.select()}
                required
                data-testid="movement-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Unidad de Compra *</Label>
              <Select 
                value={formData.input_unit} 
                onValueChange={(v) => setFormData({...formData, input_unit: v})}
              >
                <SelectTrigger data-testid="movement-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qq">Quintales (qq)</SelectItem>
                  <SelectItem value="lb">Libras (lb)</SelectItem>
                  <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                  <SelectItem value="ton">Toneladas (ton)</SelectItem>
                  <SelectItem value="saco">Sacos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Sack weight configuration when unit is saco */}
          {formData.input_unit === 'saco' && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="space-y-2">
                <Label htmlFor="purchase_sack_weight">Peso por Saco *</Label>
                <Input
                  id="purchase_sack_weight"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.sack_weight}
                  onChange={(e) => setFormData({...formData, sack_weight: parseFloat(e.target.value) || 0})}
                  placeholder="Ej: 50"
                  data-testid="movement-sack-weight-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_sack_weight_unit">Unidad del Peso</Label>
                <Select 
                  value={formData.sack_weight_unit} 
                  onValueChange={(v) => setFormData({...formData, sack_weight_unit: v})}
                >
                  <SelectTrigger data-testid="movement-sack-weight-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lb">Libras (lb)</SelectItem>
                    <SelectItem value="kg">Kilogramos (kg)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="col-span-2 text-xs text-blue-600">
                Cada saco pesa {formData.sack_weight} {formData.sack_weight_unit === 'lb' ? 'libras' : 'kilogramos'}
              </p>
            </div>
          )}

          {/* Conversion Preview */}
          {conversionPreview && formData.quantity > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-blue-800">
                <RefreshCw className="h-4 w-4" />
                <span className="font-medium text-sm">Conversión Automática</span>
              </div>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  <strong>{formData.quantity}</strong> {UNIT_LABELS[formData.input_unit]} → 
                  <strong className="text-blue-900"> {conversionPreview.quantity.toFixed(2)}</strong> {UNIT_LABELS[conversionPreview.baseUnit]}
                </p>
                {formData.unit_cost > 0 && (
                  <p className="mt-1">
                    Costo por {conversionPreview.baseUnit}: <strong>{formatCurrency(conversionPreview.costPerBaseUnit)}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Campos adicionales para ENTRADA (Compra) */}
          {formData.type === 'entrada' && (
            <>
              {/* Precio y Moneda */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Costo por {UNIT_LABELS[formData.input_unit]?.split(' ')[0] || 'Unidad'} *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unit_cost}
                    onChange={(e) => setFormData({...formData, unit_cost: parseFloat(e.target.value) || 0})}
                    onFocus={(e) => e.target.select()}
                    required
                    data-testid="movement-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Moneda *</Label>
                  <Select 
                    value={formData.currency} 
                    onValueChange={(v) => setFormData({...formData, currency: v})}
                  >
                    <SelectTrigger data-testid="movement-currency-select">
                      <SelectValue placeholder="Seleccionar moneda..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DOP">Peso Dominicano (RD$)</SelectItem>
                      <SelectItem value="USD">Dólar (US$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Fecha de compra */}
              <div className="space-y-2">
                <Label>Fecha de Compra *</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                  required
                  data-testid="movement-date-input"
                />
              </div>

              {/* Proveedor y Factura */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Proveedor *</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={formData.supplier_id} 
                      onValueChange={(v) => {
                        const supplier = suppliers.find(s => s.id === v);
                        setFormData({...formData, supplier_id: v, supplier: supplier?.name || ''});
                      }}
                    >
                      <SelectTrigger className="flex-1" data-testid="movement-supplier-select">
                        <SelectValue placeholder="Seleccionar proveedor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={onAddSupplier}
                      className="flex-shrink-0"
                      data-testid="add-supplier-quick-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>No. Factura / Referencia</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({...formData, invoice_number: e.target.value})}
                    placeholder="Ej: FAC-001234"
                    data-testid="movement-invoice-input"
                  />
                </div>
              </div>

              {/* Valor Total de la Factura */}
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-800 font-medium">
                    <DollarSign className="h-5 w-5" />
                    Valor Total de la Factura
                  </div>
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(formData.quantity * formData.unit_cost, formData.currency)}
                  </div>
                </div>
                {formData.quantity > 0 && formData.unit_cost > 0 && (
                  <div className="mt-2 pt-2 border-t border-green-200 text-sm text-green-700">
                    {formData.quantity} {formData.input_unit} × {formatCurrency(formData.unit_cost, formData.currency)} = {formatCurrency(formData.quantity * formData.unit_cost, formData.currency)}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Notas */}
          <div className="space-y-2">
            <Label>Notas / Observaciones</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Notas adicionales sobre este movimiento..."
              rows={3}
              data-testid="movement-notes-input"
            />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancelar</Button>
            </DialogClose>
            <Button type="submit" className="bg-[#931A25] hover:bg-[#7A151F]" data-testid="save-movement-btn">
              {formData.type === 'entrada' ? 'Registrar Compra' : 'Registrar Salida'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
